import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Alert } from '@factorialco/f0-react'
import moment from 'moment'

import i18n from 'modules/core/lib/i18n'

import { DayPicker } from './DayPicker'

interface Props {
  hasNewStructureEnabled: boolean
  isCreating?: boolean
  canBeMarkedAsActive: boolean
  error?: boolean
  startDateError?: boolean
  startDate: string | undefined
  endDate?: string | undefined | null
  handleSelectedDatesChange: (newDates: { startDate?: string; endDate?: string }) => void
  disabled?: boolean
}

export const DateSelectors = ({
  hasNewStructureEnabled,
  isCreating = false,
  canBeMarkedAsActive,
  error,
  startDateError,
  startDate,
  endDate,
  handleSelectedDatesChange,
  disabled = false,
}: Props) => {
  const hasError = !!error
  const hasStartDateError = !!startDateError

  const errorMessage = hasError
    ? i18n.t('finance.cost_centers.assignments.day_picker.error_overlap')
    : undefined

  const startDateErrorMessage = hasStartDateError
    ? i18n.t('finance.cost_centers.assignments.day_picker.error_start_date_in_past')
    : undefined

  const handleStartDateChange = (date: string | undefined) => {
    handleSelectedDatesChange({ startDate: date })
  }

  const handleEndDateChange = (date: string | undefined) => {
    handleSelectedDatesChange({ endDate: date })
  }

  // When hasNewStructureEnabled:
  // - Creating: start date is editable, end date is disabled (not shown/readonly)
  // - Editing: both start and end dates are disabled (readonly)
  // When disabled (single default assignment in new structure), both dates are readonly
  const isStartDateReadonly = disabled || (hasNewStructureEnabled && !isCreating)
  const isEndDateReadonly = disabled || hasNewStructureEnabled

  // Show info alert when creating with new structure and start date is selected
  const showInfoAlert = hasNewStructureEnabled && isCreating && !!startDate

  // When creating, enforce minimum start date as today
  const minStartDate = isCreating ? moment().format('YYYY-MM-DD') : undefined

  return (
    <>
      <Box flexDirection="row" gap="s24">
        <DayPicker
          readonly={isStartDateReadonly}
          selectedDate={startDate}
          handleSelectedDatesChange={handleStartDateChange}
          error={hasError || hasStartDateError}
          label={i18n.t('finance.cost_centers.assignments.day_picker.start_date')}
          minDate={minStartDate}
          maxDate={endDate || undefined}
          mandatory
        />

        <DayPicker
          readonly={isEndDateReadonly}
          minDate={startDate}
          selectedDate={endDate || undefined}
          handleSelectedDatesChange={handleEndDateChange}
          error={hasError}
          label={i18n.t('finance.cost_centers.assignments.day_picker.end_date')}
          mandatory={!canBeMarkedAsActive}
        />
      </Box>
      {hasStartDateError && (
        <Text.Normal size="200" color="radicalRed1000">
          {startDateErrorMessage}
        </Text.Normal>
      )}
      {showInfoAlert && (
        <F0Alert
          title={i18n.t(
            'finance.cost_centers.assignments.structured.end_current_assignment_info.title'
          )}
          description={i18n.t(
            'finance.cost_centers.assignments.structured.end_current_assignment_info.description'
          )}
          variant="info"
        />
      )}
      {hasError && (
        <Text.Normal size="200" color="radicalRed1000">
          {errorMessage}
        </Text.Normal>
      )}
    </>
  )
}
